import React from 'react';
import Icon from '../../../components/AppIcon';

const RolePermissions = ({ role }) => {
  const rolePermissions = {
    reporter: {
      title: 'Reporter',
      description: 'Create and edit news articles with AI assistance',
      permissions: [
        { name: 'Generate News Articles', icon: 'FileText', allowed: true },
        { name: 'Fact Check Content', icon: 'CheckCircle', allowed: true },
        { name: 'Bias Detection Analysis', icon: 'Search', allowed: true },
        { name: 'Edit Own Articles', icon: 'Edit', allowed: true },
        { name: 'Publish Articles', icon: 'Send', allowed: false },
        { name: 'Manage Users', icon: 'Users', allowed: false }
      ],
      color: 'primary'
    },
    editor: {
      title: 'Editor',
      description: 'Review, edit, and publish content with full editorial control',
      permissions: [
        { name: 'Generate News Articles', icon: 'FileText', allowed: true },
        { name: 'Fact Check Content', icon: 'CheckCircle', allowed: true },
        { name: 'Bias Detection Analysis', icon: 'Search', allowed: true },
        { name: 'Edit All Articles', icon: 'Edit', allowed: true },
        { name: 'Publish Articles', icon: 'Send', allowed: true },
        { name: 'Manage Users', icon: 'Users', allowed: false }
      ],
      color: 'secondary'
    },
    viewer: {
      title: 'Viewer',
      description: 'Read-only access to published content and basic tools',
      permissions: [
        { name: 'Generate News Articles', icon: 'FileText', allowed: false },
        { name: 'Fact Check Content', icon: 'CheckCircle', allowed: true },
        { name: 'Bias Detection Analysis', icon: 'Search', allowed: true },
        { name: 'Edit Articles', icon: 'Edit', allowed: false },
        { name: 'Publish Articles', icon: 'Send', allowed: false },
        { name: 'Manage Users', icon: 'Users', allowed: false }
      ],
      color: 'accent'
    }
  };

  const currentRole = rolePermissions?.[role?.toLowerCase()] || rolePermissions?.viewer;
  const colorClass = `var(--color-${currentRole?.color})`;

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Shield" size={24} color={colorClass} />
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Role & Permissions
          </h2>
          <p className="text-sm text-muted-foreground">
            Your current access level and capabilities
          </p>
        </div>
      </div>
      {/* Role Badge */}
      <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium border mb-6 bg-${currentRole?.color}/10 text-${currentRole?.color} border-${currentRole?.color}/20`}>
        <Icon name="Crown" size={16} />
        <span>{currentRole?.title}</span>
      </div>
      <p className="text-muted-foreground mb-6">
        {currentRole?.description}
      </p>
      {/* Permissions List */}
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-foreground mb-4">Access Permissions</h3>
        
        {currentRole?.permissions?.map((permission, index) => (
          <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
            <div className="flex items-center space-x-3">
              <Icon 
                name={permission?.icon} 
                size={18} 
                color={permission?.allowed ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
              />
              <span className="text-sm font-medium text-foreground">
                {permission?.name}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {permission?.allowed ? (
                <div className="flex items-center space-x-1 text-success">
                  <Icon name="Check" size={16} />
                  <span className="text-xs font-medium">Allowed</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-muted-foreground">
                  <Icon name="X" size={16} />
                  <span className="text-xs font-medium">Restricted</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Upgrade Notice for Viewers */}
      {role?.toLowerCase() === 'viewer' && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="var(--color-primary)" />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Need More Access?
              </h4>
              <p className="text-xs text-muted-foreground">
                Contact your administrator to upgrade your role and unlock additional features.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissions;